import { useEffect, useMemo, useState } from 'react';
import { Icon } from '../../components/Icons.jsx';
import { friendlyErrorMessage, notify } from '@/lib/notifications';
import { PlatformIcon } from './shared/PlatformIcon.jsx';
import { PLATFORM_LABELS } from './shared/platformLabels.js';
import { api } from '../../services/api/client.js';

const WINDOW_OPTIONS = [
  { value: 7, label: '7 days' },
  { value: 14, label: '14 days' },
  { value: 30, label: '30 days' },
  { value: 90, label: '90 days' },
];

export function AnalyticsTab({ platformPrefs = [], onJumpTo }) {
  const [windowDays, setWindowDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [insights, setInsights] = useState(null);
  const [missingEndpoint, setMissingEndpoint] = useState(false);
  const [recommendationStates, setRecommendationStates] = useState({});

  async function load(window) {
    try {
      setLoading(true);
      const result = await api.analytics.getInsights({ windowDays: window });
      const payload = result.insights || result;
      setInsights(payload || null);
      setMissingEndpoint(false);
    } catch (err) {
      if (err?.status === 404 || err?.code === 'NOT_IMPLEMENTED') {
        setMissingEndpoint(true);
        setInsights(null);
      } else {
        notify('analytics.loadFailed', { error: err });
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load(windowDays);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowDays]);

  const platformSummaries = insights?.platformSummaries || [];
  const topPosts = insights?.topPosts || [];
  const warnings = insights?.warnings || [];
  const recommendations = (insights?.recommendations || []).filter((item) => recommendationStates[item.id]?.status !== 'dismissed');

  const kpis = useMemo(() => buildKpis(platformSummaries), [platformSummaries]);
  const reachRows = useMemo(() => buildReachRows(platformSummaries), [platformSummaries]);
  const engagementSegments = useMemo(() => buildEngagementSegments(platformSummaries), [platformSummaries]);

  return (
    <>
      <div className="card ds-card-pad settings-card">
        <div
          className="settings-card-header"
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}
        >
          <div>
            <h3 className="settings-card-title">Performance</h3>
            <p className="settings-card-description">Reach, reactions, and replies across your connected platforms.</p>
          </div>
          <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <div className="cp-window-toggle" role="group" aria-label="Time window">
              {WINDOW_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={'cp-window-btn ' + (windowDays === opt.value ? 'is-active' : '')}
                  onClick={() => setWindowDays(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <button className="btn btn-ghost btn-sm" onClick={() => load(windowDays)} disabled={loading}>
              <Icon name="refresh" size={13} /> {loading ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        </div>

        {loading ? (
          <KpiSkeleton />
        ) : missingEndpoint ? (
          <PendingState />
        ) : !platformSummaries.length ? (
          <EmptyState onJumpTo={onJumpTo} />
        ) : (
          <>
            <div className="ap-stats-grid cp-kpi-grid">
              {kpis.map((kpi) => (
                <KpiCard key={kpi.label} {...kpi} />
              ))}
            </div>
            {warnings.length > 0 && (
              <div className="cp-warning-row">
                {warnings.map((w, i) => (
                  <div key={i} className="auth-error" style={{ marginTop: 12 }}>
                    <Icon name="alert" size={14} /> {w}
                  </div>
                ))}
              </div>
            )}
            {insights?.summary && (
              <div className="cp-insight-summary">
                <div className="settings-section-label">Summary</div>
                <p className="body" style={{ margin: 0 }}>{insights.summary}</p>
              </div>
            )}
            {recommendations.length > 0 && (
              <RecommendationPanel
                recommendations={recommendations}
                states={recommendationStates}
                setStates={setRecommendationStates}
                windowDays={windowDays}
              />
            )}
            <PerformanceCharts reachRows={reachRows} engagementSegments={engagementSegments} />
          </>
        )}
      </div>

      {!loading && !missingEndpoint && platformSummaries.length > 0 && (
        <div className="card ds-card-pad settings-card">
          <div className="settings-card-header">
            <h3 className="settings-card-title">Per-platform breakdown</h3>
            <p className="settings-card-description">Median performance per channel for the selected window.</p>
          </div>
          <div className="cp-platform-stats">
            {platformSummaries.map((row) => (
              <PlatformStatCard key={row.platform} row={row} prefs={platformPrefs.find((p) => p.platform === row.platform)} />
            ))}
          </div>
        </div>
      )}

      {!loading && !missingEndpoint && topPosts.length > 0 && (
        <div className="card ds-card-pad settings-card">
          <div className="settings-card-header">
            <h3 className="settings-card-title">Top posts</h3>
            <p className="settings-card-description">Highest-performing pieces in this window — patterns to reuse.</p>
          </div>
          <div className="cp-top-posts">
            {topPosts.slice(0, 6).map((post, i) => (
              <TopPostRow key={post.contentId || i} post={post} rank={i + 1} />
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function RecommendationPanel({ recommendations, states, setStates, windowDays }) {
  return (
    <section className="cp-recommendation-panel">
      <div className="settings-section-label">Recommended next actions</div>
      <div className="cp-recommendation-grid">
        {recommendations.slice(0, 6).map((recommendation) => (
          <RecommendationCard
            key={recommendation.id}
            recommendation={recommendation}
            state={states[recommendation.id] || {}}
            setState={(next) => setStates(prev => ({ ...prev, [recommendation.id]: { ...(prev[recommendation.id] || {}), ...next } }))}
            windowDays={windowDays}
          />
        ))}
      </div>
    </section>
  );
}

function RecommendationCard({ recommendation, state, setState, windowDays }) {
  const action = recommendation.assistantAction;
  const limitedData = recommendation.limitedData === true;
  const busy = state.status === 'confirming' || state.status === 'notifying' || state.status === 'asking';

  async function confirm() {
    if (!action?.type) return;
    setState({ status: 'confirming', error: null });
    try {
      const result = await api.assistant.confirmAction({ action, confirmed: true });
      setState({ status: 'confirmed', confirmation: result, error: null });
    } catch (err) {
      setState({ status: 'error', error: friendlyErrorMessage(err, 'Confirmation failed. No changes were made.') });
    }
  }

  async function notify() {
    setState({ status: 'notifying', error: null });
    try {
      await api.analytics.createRecommendationNotifications({ windowDays, recommendationIds: [recommendation.id] });
      setState({ status: 'notified', error: null });
    } catch (err) {
      setState({ status: 'error', error: friendlyErrorMessage(err, 'Could not create notification.') });
    }
  }

  async function dismiss() {
    setState({ status: 'dismissed', error: null });
    try {
      await api.notifications.dismissRecommendation(recommendation.id);
    } catch {
      // Local dismissal still stands; backend may not have created a notification yet.
    }
  }

  async function askWhy() {
    setState({ status: 'asking', error: null });
    try {
      const response = await api.assistant.chat({
        message: `Explain the reasoning for this analytics recommendation without guaranteeing performance: ${recommendation.title}. ${recommendation.reason}`,
        currentScreen: 'analytics',
        selectedEntity: { type: 'analyticsRecommendation', id: recommendation.id }
      });
      setState({ status: 'explained', explanation: response?.message || 'No explanation returned.', error: null });
    } catch (err) {
      setState({ status: 'error', error: friendlyErrorMessage(err, 'Ask AI could not explain this recommendation.') });
    }
  }

  return (
    <article className="cp-recommendation-card">
      <div className="cp-recommendation-head">
        <div>
          <h4>{recommendation.title}</h4>
          <p>{recommendation.message}</p>
        </div>
        <span className={'cp-confidence-pill is-' + (recommendation.confidence || 'low')}>{recommendation.confidence || 'low'} confidence</span>
      </div>
      <div className="cp-recommendation-reason">
        <Icon name="sparkles" size={13} />
        <span>{recommendation.reason}</span>
      </div>
      <div className="cp-recommendation-meta">
        <span>Type: {formatRecommendationType(recommendation.type)}</span>
        <span>Sample: {recommendation.sampleSize || 0}</span>
        {limitedData && <span>Limited data</span>}
      </div>
      {limitedData && (
        <div className="cp-limited-data-note">Data is limited. Treat this as directional and confirm before changing strategy or using plan quota.</div>
      )}
      <div className="cp-recommendation-actions">
        <button className="btn btn-primary btn-sm" onClick={confirm} disabled={busy || !action?.type}>
          {state.status === 'confirming' ? 'Confirming...' : 'Confirm action'}
        </button>
        <button className="btn btn-secondary btn-sm" onClick={askWhy} disabled={busy}>Ask AI why</button>
        <button className="btn btn-ghost btn-sm" onClick={notify} disabled={busy}>{state.status === 'notifying' ? 'Creating...' : 'Notify me'}</button>
        <button className="btn btn-ghost btn-sm" onClick={dismiss} disabled={busy}>Dismiss</button>
      </div>
      {state.status === 'confirmed' && (
        <div className="cp-recommendation-result">
          {state.confirmation?.executed
            ? (state.confirmation.message || 'Backend executed this action.')
            : (state.confirmation?.message || 'Backend validated this action. No changes were made.')}
        </div>
      )}
      {state.status === 'notified' && <div className="cp-recommendation-result">Smart notification created. No strategy or content changed.</div>}
      {state.explanation && <div className="cp-recommendation-explanation">{state.explanation}</div>}
      {state.error && <div className="auth-error" style={{ marginTop: 10 }}>{state.error}</div>}
    </article>
  );
}

function KpiCard({ label, value, delta, tone = 'green', icon }) {
  return (
    <div className="card ap-stat-card cp-kpi-card">
      <div className="row between" style={{ alignItems: 'center' }}>
        <div className="eyebrow">{label}</div>
        {icon && (
          <span className="cp-kpi-icon" aria-hidden="true">
            <Icon name={icon} size={14} />
          </span>
        )}
      </div>
      <div className="h1" style={{ marginTop: 8, fontSize: 28, lineHeight: 1.1 }}>{value}</div>
      {delta && <div className={'caption tone-' + tone} style={{ marginTop: 6 }}>{delta}</div>}
    </div>
  );
}

function PerformanceCharts({ reachRows, engagementSegments }) {
  if (!reachRows.length) return null;
  return (
    <div className="cp-chart-grid" aria-label="Performance charts">
      <PlatformReachChart rows={reachRows} />
      <EngagementMixChart segments={engagementSegments} />
    </div>
  );
}

function PlatformReachChart({ rows }) {
  const max = Math.max(...rows.map((row) => row.value), 1);
  const height = 76 + rows.length * 34;
  const leader = rows[0];

  return (
    <article className="cp-chart-card">
      <div className="cp-chart-head">
        <div>
          <div className="settings-section-label">Reach by platform</div>
          <p className="caption muted">Median views multiplied by posts in this window.</p>
        </div>
      </div>
      <svg
        className="cp-chart-svg"
        viewBox={`0 0 640 ${height}`}
        role="img"
        aria-label={`Reach by platform. ${leader.label} leads with ${formatNumber(leader.value)} estimated reach.`}
      >
        <line className="cp-chart-axis" x1="160" y1="42" x2="160" y2={height - 20} />
        {rows.map((row, i) => {
          const y = 58 + i * 34;
          const width = row.value > 0 ? Math.max(4, (row.value / max) * 342) : 0;
          const label = row.label.length > 17 ? `${row.label.slice(0, 16)}...` : row.label;
          return (
            <g key={row.platform}>
              <text className="cp-chart-label" x="20" y={y}>{label}</text>
              <rect className="cp-chart-track-svg" x="160" y={y - 13} width="342" height="16" rx="8" />
              <rect className="cp-chart-bar cp-chart-bar-violet" x="160" y={y - 13} width={width} height="16" rx="8" />
              <text className="cp-chart-value" x="620" y={y}>{formatNumber(row.value)}</text>
            </g>
          );
        })}
      </svg>
    </article>
  );
}

function EngagementMixChart({ segments }) {
  const total = segments.reduce((sum, seg) => sum + seg.value, 0);
  let cursor = 0;
  const rects = segments.map((seg) => {
    const width = total > 0 ? (seg.value / total) * 592 : 0;
    const x = 24 + cursor;
    cursor += width;
    return { ...seg, x, width };
  });

  return (
    <article className="cp-chart-card">
      <div className="cp-chart-head">
        <div>
          <div className="settings-section-label">Engagement mix</div>
          <p className="caption muted">Reactions, comments, and shares from median post performance.</p>
        </div>
      </div>
      <svg
        className="cp-chart-svg"
        viewBox="0 0 640 220"
        role="img"
        aria-label={`Engagement mix totals ${formatNumber(total)} interactions.`}
      >
        <text className="cp-chart-total" x="24" y="42">{formatNumber(total)}</text>
        <text className="cp-chart-muted" x="24" y="62">total interactions</text>
        <rect className="cp-chart-track-svg" x="24" y="82" width="592" height="24" rx="12" />
        {rects.map((seg) => seg.width > 0 && (
          <rect
            key={seg.key}
            className={'cp-chart-bar cp-chart-bar-' + seg.tone}
            x={seg.x}
            y="82"
            width={seg.width}
            height="24"
            rx="12"
          />
        ))}
        {segments.map((seg, i) => {
          const pct = total > 0 ? (seg.value / total) * 100 : 0;
          const y = 136 + i * 25;
          return (
            <g key={seg.key}>
              <circle className={'cp-chart-dot cp-chart-dot-' + seg.tone} cx="30" cy={y - 4} r="5" />
              <text className="cp-chart-label" x="44" y={y}>{seg.label}</text>
              <text className="cp-chart-value" x="616" y={y}>{formatNumber(seg.value)} · {pct.toFixed(1)}%</text>
            </g>
          );
        })}
      </svg>
    </article>
  );
}

function PlatformStatCard({ row, prefs }) {
  const enabled = !!prefs?.enabled;
  const engagementPct = formatRate(row.reactionRate ?? row.engagementRate);
  const positivePct = formatRate(row.positiveReactionRate);
  const commentPct = formatRate(row.commentRate);

  return (
    <div className={'cp-platform-stat ' + (enabled ? '' : 'is-disabled')}>
      <div className="cp-platform-stat-head">
        <div className="row" style={{ gap: 10, alignItems: 'center' }}>
          <PlatformIcon platform={row.platform} size={18} />
          <strong>{PLATFORM_LABELS[row.platform] || row.platform}</strong>
        </div>
        <span className="caption muted">{row.postCount ?? 0} posts</span>
      </div>
      <div className="cp-platform-stat-grid">
        <Metric label="Median views" value={formatNumber(row.medianViews)} />
        <Metric label="Median reactions" value={formatNumber(row.medianReactions)} />
        <Metric label="Median comments" value={formatNumber(row.medianComments)} />
      </div>
      <div className="cp-platform-stat-bars">
        <RateBar label="Engagement" pct={engagementPct} tone="violet" />
        <RateBar label="Positive sentiment" pct={positivePct} tone="green" />
        <RateBar label="Comments" pct={commentPct} tone="blue" />
      </div>
      {Array.isArray(row.notes) && row.notes.length > 0 && (
        <ul className="cp-bullet-list cp-platform-stat-notes">
          {row.notes.slice(0, 3).map((note, i) => (
            <li key={i}>{note}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="cp-metric">
      <div className="caption muted">{label}</div>
      <div className="cp-metric-value">{value}</div>
    </div>
  );
}

function RateBar({ label, pct, tone }) {
  const safe = pct == null ? 0 : Math.max(0, Math.min(100, pct));
  return (
    <div className="cp-rate-bar">
      <div className="cp-rate-bar-label">
        <span>{label}</span>
        <span className="caption muted">{pct == null ? '—' : `${safe.toFixed(1)}%`}</span>
      </div>
      <div className="cp-rate-bar-track">
        <div className={'cp-rate-bar-fill cp-rate-bar-fill-' + tone} style={{ width: `${safe}%` }} />
      </div>
    </div>
  );
}

function TopPostRow({ post, rank }) {
  const reach = post.views ?? null;
  const reactions = post.reactionsTotal ?? post.likes ?? null;
  const comments = post.comments ?? null;
  const shares = post.shares ?? null;

  return (
    <div className="cp-top-post">
      <div className="cp-top-post-rank">#{rank}</div>
      <div className="cp-top-post-body">
        <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {post.platform && <PlatformIcon platform={post.platform} size={14} />}
          {post.platform && (
            <span className="chip" style={{ padding: '2px 8px', fontSize: 11 }}>
              {PLATFORM_LABELS[post.platform] || post.platform}
            </span>
          )}
          {post.topic && <strong className="cp-top-post-topic">{post.topic}</strong>}
        </div>
        {post.hook && <p className="caption muted cp-top-post-hook">{post.hook}</p>}
        <div className="cp-top-post-metrics">
          {reach != null && <Stat icon="eye" label="Reach" value={formatNumber(reach)} />}
          {reactions != null && <Stat icon="heart" label="Reactions" value={formatNumber(reactions)} />}
          {comments != null && <Stat icon="msg" label="Comments" value={formatNumber(comments)} />}
          {shares != null && <Stat icon="send" label="Shares" value={formatNumber(shares)} />}
        </div>
        {post.whyItWorked && (
          <blockquote className="cp-top-post-why">
            <Icon name="sparkles" size={12} />
            <span>{post.whyItWorked}</span>
          </blockquote>
        )}
      </div>
    </div>
  );
}

function Stat({ icon, label, value }) {
  return (
    <span className="cp-top-post-stat" title={label}>
      <Icon name={icon} size={12} /> {value}
    </span>
  );
}

function KpiSkeleton() {
  return (
    <div className="ap-stats-grid cp-kpi-grid">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="card ap-stat-card cp-kpi-card cp-kpi-skeleton" aria-hidden="true">
          <div className="cp-skeleton-line" style={{ width: 80 }} />
          <div className="cp-skeleton-line cp-skeleton-line-lg" style={{ marginTop: 10 }} />
          <div className="cp-skeleton-line" style={{ width: 60, marginTop: 8 }} />
        </div>
      ))}
    </div>
  );
}

function EmptyState({ onJumpTo }) {
  return (
    <div className="cp-empty-state">
      <div className="cp-empty-icon" aria-hidden="true">
        <Icon name="target" size={20} />
      </div>
      <h4 className="h3" style={{ margin: '12px 0 4px' }}>No insights yet</h4>
      <p className="caption muted" style={{ maxWidth: 420 }}>
        Connect your social accounts and publish posts. Metrics will appear here within a few hours of your first publish.
      </p>
      <div className="row" style={{ gap: 8, marginTop: 16 }}>
        <button className="btn btn-secondary btn-sm" onClick={() => onJumpTo?.('rules')}>
          <Icon name="plug" size={12} /> Configure platforms
        </button>
        <button className="btn btn-primary btn-sm" onClick={() => onJumpTo?.('month')}>
          <Icon name="calendar" size={12} /> Plan your month
        </button>
      </div>
    </div>
  );
}

function PendingState() {
  return (
    <div className="cp-empty-state">
      <div className="cp-empty-icon" aria-hidden="true">
        <Icon name="info" size={20} />
      </div>
      <h4 className="h3" style={{ margin: '12px 0 4px' }}>Analytics arriving soon</h4>
      <p className="caption muted" style={{ maxWidth: 460 }}>
        The analytics endpoint is rolling out. Once it's live, you'll see reach, reactions, comments, and top posts here automatically — no setup on your side.
      </p>
    </div>
  );
}

function buildReachRows(platformSummaries) {
  return platformSummaries
    .map((row) => {
      const posts = Number(row.postCount) || 0;
      const medianViews = Number(row.medianViews) || 0;
      return {
        platform: row.platform,
        label: PLATFORM_LABELS[row.platform] || row.platform,
        value: medianViews * posts,
        posts,
      };
    })
    .filter((row) => row.posts > 0 || row.value > 0)
    .sort((a, b) => b.value - a.value)
    .slice(0, 6);
}

function buildEngagementSegments(platformSummaries) {
  const totals = platformSummaries.reduce((acc, row) => {
    const posts = Number(row.postCount) || 0;
    acc.reactions += (Number(row.medianReactions) || 0) * posts;
    acc.comments += (Number(row.medianComments) || 0) * posts;
    acc.shares += (Number(row.medianShares) || 0) * posts;
    return acc;
  }, { reactions: 0, comments: 0, shares: 0 });

  return [
    { key: 'reactions', label: 'Reactions', value: totals.reactions, tone: 'violet' },
    { key: 'comments', label: 'Comments', value: totals.comments, tone: 'green' },
    { key: 'shares', label: 'Shares', value: totals.shares, tone: 'blue' },
  ];
}

function buildKpis(platformSummaries) {
  if (!platformSummaries.length) return [];

  let totalPosts = 0;
  let totalViews = 0;
  let totalReactions = 0;
  let totalComments = 0;
  let totalShares = 0;
  let weightedRate = 0;
  let weightSum = 0;
  let topPlatform = null;
  let topPlatformReach = -1;

  for (const row of platformSummaries) {
    const posts = Number(row.postCount) || 0;
    totalPosts += posts;
    const views = Number(row.medianViews) || 0;
    totalViews += views * posts;
    totalReactions += (Number(row.medianReactions) || 0) * posts;
    totalComments += (Number(row.medianComments) || 0) * posts;
    totalShares += (Number(row.medianShares) || 0) * posts;
    if (row.reactionRate != null && posts > 0) {
      weightedRate += Number(row.reactionRate) * posts;
      weightSum += posts;
    }
    const reach = views * posts;
    if (reach > topPlatformReach) {
      topPlatformReach = reach;
      topPlatform = row.platform;
    }
  }

  const avgEngagement = weightSum > 0 ? (weightedRate / weightSum) : null;
  const totalEngagement = totalReactions + totalComments + totalShares;

  return [
    {
      label: 'Total reach',
      value: formatNumber(totalViews),
      delta: `${totalPosts} posts`,
      tone: 'purple',
      icon: 'eye',
    },
    {
      label: 'Engagement',
      value: formatNumber(totalEngagement),
      delta: `${formatNumber(totalReactions)} reactions · ${formatNumber(totalComments)} comments`,
      tone: 'violet',
      icon: 'heart',
    },
    {
      label: 'Engagement rate',
      value: avgEngagement == null ? '—' : `${(avgEngagement * 100).toFixed(1)}%`,
      delta: avgEngagement == null ? 'Not enough data' : 'Median across platforms',
      tone: 'green',
      icon: 'flame',
    },
    {
      label: 'Top platform',
      value: topPlatform ? PLATFORM_LABELS[topPlatform] || topPlatform : '—',
      delta: topPlatform ? `${formatNumber(topPlatformReach)} reach` : 'Awaiting data',
      tone: 'green',
      icon: 'target',
    },
  ];
}

function formatNumber(value) {
  if (value == null) return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 10_000) return `${(n / 1_000).toFixed(1)}k`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(2)}k`;
  return Math.round(n).toString();
}

function formatRate(value) {
  if (value == null) return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  // Accept both 0-1 fractions and 0-100 percentages
  return n > 1 ? n : n * 100;
}

function formatRecommendationType(value = '') {
  return String(value).replace(/_/g, ' ');
}
